import { Component, Inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatSelectChange } from '@angular/material/select';
import { ICostumUdf, IUdfToReturn, IUdfValue, IUdfWithValue } from '@app/interfaces/i-udf';
import { CustomUdfPresentationService } from '@app/services/custom-udf-presentation.service';
import { AlertsService, CLToastType, ModalService } from '@clavisco/alerts';
import { CLPrint, Structures } from '@clavisco/core';
import { CL_CHANNEL, ICLCallbacksInterface, ICLEvent, LinkerService, Register, Run, StepDown } from '@clavisco/linker';
import { OverlayService } from '@clavisco/overlay';
import { finalize, Subscription } from 'rxjs';

@Component({
  selector: 'app-custom-udf-presentation',
  templateUrl: './custom-udf-presentation.component.html',
  styleUrls: ['./custom-udf-presentation.component.scss']
})
export class CustomUdfPresentationComponent implements OnInit {

  /**
   * Component identifier
   */
  @Input() Id!: string;

  /**
   * Text to display at the top of the component
   */
  @Input() Title!: string;

  /**
   * URL of the API to which the component will connect
   */
  @Input() ApiUrl: string = '';

  /**
   * Category of UDFs to be queried. Example: OINV, ORDR, OQUT
   */
  @Input() UdfsCategory!: string;

  /**
   * Group of UDFs to display
   */
  @Input() UdfsGroup: string = '';

  /**
   * Son los valores de los UDFs que el usuario va a insertar mediante LinkerService
   */
  udfsInputValuesToSet: IUdfWithValue[] | undefined;

  filteredTypeheadOptions: IUdfValue[] = [];
  currentTypeheadOptions: IUdfValue[] = [];
  Udfs!: IUdfWithValue[];
  someUdfToRender: boolean = false;

  renderedUdfsForm!: FormGroup;
  renderedUdfsFormHasControls: boolean = false;


  callbacks: ICLCallbacksInterface<CL_CHANNEL> = {
    Callbacks: {},
    Tracks: [],
  };

  allSubscriptions$: Subscription;
  constructor(private overlayService: OverlayService,
              private alertsService: AlertsService,
              private dynamicUdfsPresentationService: CustomUdfPresentationService,
              private modalService: ModalService,
              @Inject('LinkerService') private linkerService: LinkerService) {
    this.allSubscriptions$ = new Subscription();
  }

  ngOnDestroy(): void {
    this.allSubscriptions$.unsubscribe();
  }

  ngOnInit(): void
  {
    Register<CL_CHANNEL>(this.Id, CL_CHANNEL.INFLATE, this.OnInflateUDFs, this.callbacks);

    Register(this.Id, CL_CHANNEL.DATA_LINE_1, this.OutputUdfData, this.callbacks);

    Register(this.Id, CL_CHANNEL.DATA_LINE_2, this.OutputNoValidateUdfData, this.callbacks);

    Register(this.Id, CL_CHANNEL.RESET, this.CleanFields, this.callbacks);

    if(!this.Id) CLPrint('Component id was not provided', Structures.Enums.CL_DISPLAY.ERROR);

    this.Udfs = [];

    this.GetUdfs();

    this.allSubscriptions$.add(this.linkerService.Flow()?.pipe(
      StepDown<CL_CHANNEL>(this.callbacks),
    ).subscribe({
      next: callback => Run(callback.Target, callback, this.callbacks.Callbacks),
      error: error => CLPrint(`${error}`, Structures.Enums.CL_DISPLAY.ERROR)
    }));
  }

  /**
   * Defines the form controls for the rendered UDFs based on the UDFs that are marked as rendered.
   */
  DefineRenderedUdfsFormControls(): void
  {
    this.renderedUdfsForm = new FormGroup({});

    this.Udfs.filter(u => u.IsRendered).forEach(u => {
      this.renderedUdfsForm.addControl(u.Name, new FormControl(u.Value));
      if(u.IsRequired)
      {
        this.renderedUdfsForm.get(u.Name)?.addValidators(Validators.required);
      }
    });

    this.renderedUdfsFormHasControls = true;

    this.ListenTypeaheadToFilterValues();
  }

  /**
   * Outputs the UDF data to the parent component or service.
   */
  OutputUdfData = (_event: ICLEvent): void =>
  {
    if(this.renderedUdfsForm && this.renderedUdfsForm.invalid)
    {
      this.alertsService.Toast({type: CLToastType.INFO, message: 'Hay algunos campos requeridos sin completar'});
      this.renderedUdfsForm.markAllAsTouched();
      return;
    }

    this.linkerService.Publish({
      CallBack: CL_CHANNEL.OUTPUT,
      Data: JSON.stringify(this.Udfs.map(u => ({
        Name: u.Name,
        FieldType: u.FieldType,
        Value: u.Value
      } as IUdfToReturn))),
      Target: this.Id
    });
  }

  /**
   * Outputs the UDF data without validation to the parent component or service.
   */
  OutputNoValidateUdfData = (_event: ICLEvent): void =>
  {
    this.linkerService.Publish({
      CallBack: CL_CHANNEL.OUTPUT,
      Data: JSON.stringify(this.Udfs.map(u => ({
        Name: u.Name,
        FieldType: u.FieldType,
        Value: u.Value
      } as IUdfToReturn))),
      Target: this.Id
    });
  }

  /**
   * This method resets the form controls of the rendered UDFs.
   * @param _event Event triggered to clean the fields of the rendered UDFs form.
   */
  CleanFields = (_event: ICLEvent): void => {
    this.renderedUdfsForm?.reset('');
    this.Udfs?.forEach(udf => {
      udf.Value = null;
    });
  }

  /**
   * This method retrieves the UDFs based on the provided category and group.
   * @returns void
   */
  GetUdfs(): void
  {
    if(!this.UdfsCategory)
    {
      CLPrint('The category was not defined', Structures.Enums.CL_DISPLAY.ERROR)
      return;
    }

    this.overlayService.OnGet();
    this.dynamicUdfsPresentationService.GetUdfs(this.ApiUrl, this.UdfsCategory, this.UdfsGroup)
    .pipe(
      finalize(()=> this.overlayService.Drop())
    )
    .subscribe({
      next: (callback) => {
        if(callback.Data)
        {
          this.Udfs = [...callback.Data].map(u => ({...u, ParsedValues: this.GetUdfValidValues(u.Values), Value: null}));
          this.someUdfToRender = this.Udfs.some(u => u.IsRendered);
          this.DefineRenderedUdfsFormControls();
          this.SetUdfsInputValues();
        }
        else
        {
          CLPrint(callback.Message || 'An error occurred while getting the UDFs', Structures.Enums.CL_DISPLAY.ERROR);
        }
        this.linkerService.Publish({
          CallBack: CL_CHANNEL.OUTPUT_2,
          Target: this.Id,
          Data: JSON.stringify(this.Udfs)
        });
      },
      error: (error) => {
        this.linkerService.Publish({
          CallBack: CL_CHANNEL.OUTPUT_2,
          Target: this.Id,
          Data: JSON.stringify([])
        });
        CLPrint(error, Structures.Enums.CL_DISPLAY.ERROR);
      }
    });

  }

  /**
   * Parses the UDF valid values from a JSON string into an array of IUdfValue objects.
   */
  GetUdfValidValues(_udfValues: string): IUdfValue[]
  {
    if(!_udfValues) return [];

    return JSON.parse(_udfValues) as IUdfValue[];
  }

  /**
   * This method updates the UDF value based on the event and calls OnRenderedUdfChange to handle any post-transaction logic.
   * @param _event 
   * @param _udf 
   */
  OnUdfValueChange(_event: any, _udf: IUdfWithValue): void
  {
    let udfValue = null;

    if(_event instanceof MatSelectChange)
    {
      udfValue = _event.value;
    }
    else if(_event instanceof MatAutocompleteSelectedEvent)
    {
      udfValue = (_event.option.value as IUdfValue).Value;
    }
    else
    {
      udfValue = _event.target.value;
    }

    if(this.Udfs.some(u => u.Name === _udf.Name))
    {
      this.Udfs.find(u => u.Name === _udf.Name)!.Value = udfValue;
    }

    this.OnRenderedUdfChange(udfValue, _udf);
  }


  /**
   * This method handles the change of a rendered UDF value and updates the target UDF based on the post-transaction object.
   * @param _value 
   * @param _udf 
   * @returns 
   */
  public OnRenderedUdfChange(_value: any, _udf: ICostumUdf): void {

    let value = _udf.IsTypehead ? _udf.ParsedValues.find(v => v.Value === _value) : _value;

    this.renderedUdfsForm.get(_udf.Name)?.patchValue(value);

    const TARGET_UDF = this.Udfs.find(x => x.Name === _udf.TargetToOverride);

    if (!TARGET_UDF)
    {
      CLPrint('The target UDF was not found', Structures.Enums.CL_DISPLAY.INFORMATION);
      return;
    }

    const DBO_TYPE = !TARGET_UDF.DataSource && !TARGET_UDF.ParsedValues ? "FN" : "SP";

    this.overlayService.OnGet();

    this.dynamicUdfsPresentationService.GetUdfValueByDBO(_udf.PostTransactionObject, _value, DBO_TYPE, this.ApiUrl)
      .pipe(
        finalize(() => this.overlayService.Drop())
      )
      .subscribe({
        next: (callback) => {
          this.alertsService.ShowAlert({Response: callback});

          if (callback.Data)
          {
            if (DBO_TYPE === 'FN')
            {
              let valueToSet = this.udfsInputValuesToSet?.find(u => u.Name === _udf.TargetToOverride)?.Value;

              if(TARGET_UDF.IsTypehead)
              {
                let typeaheadValue = callback.Data.find(u => u.Value === valueToSet) || '';
                this.renderedUdfsForm.get(_udf.TargetToOverride)?.patchValue(typeof(typeaheadValue) === 'string' ? typeaheadValue : typeaheadValue);
                this.Udfs.find(u => u.Name === _udf.TargetToOverride)!.Value = typeof(typeaheadValue) === 'string' ? typeaheadValue : typeaheadValue.Value;
              }
            }
            else
            {
              let valueToSet = this.udfsInputValuesToSet?.find(u => u.Name === _udf.TargetToOverride)?.Value || "";

              const TARGET_INDEX: number = this.Udfs.findIndex(x => x.Name === TARGET_UDF.Name);

              this.Udfs[TARGET_INDEX].ParsedValues = callback.Data;

              if(TARGET_UDF.IsTypehead)
              {
                let typeaheadValue = callback.Data.find(u => u.Value === valueToSet) || '';
                this.renderedUdfsForm.get(_udf.TargetToOverride)?.patchValue(typeof(typeaheadValue) === 'string' ? typeaheadValue : typeaheadValue);
                this.Udfs.find(u => u.Name === _udf.TargetToOverride)!.Value = typeof(typeaheadValue) === 'string' ? typeaheadValue : typeaheadValue.Value;
              }
            }
          }

        },
        error: error => {
          this.alertsService.ShowAlert({HttpErrorResponse: error});
        }
      });
  }

  /**
   * This method filters the typeahead options based on the current UDF value.
   */
  SetFilteredOptions(_udf: IUdfWithValue): void
  {
    this.currentTypeheadOptions = [..._udf.ParsedValues];

    let currentTypeaheadValue: IUdfValue | string = this.renderedUdfsForm.get(_udf.Name)?.value;

    if(!currentTypeaheadValue)
    {
      this.filteredTypeheadOptions = [..._udf.ParsedValues];
      return;
    }

    let mappedValue = typeof currentTypeaheadValue === 'string' ? currentTypeaheadValue : `${currentTypeaheadValue.Value} - ${currentTypeaheadValue.Description}`;

    this.filteredTypeheadOptions = this.currentTypeheadOptions.filter(cto => `${cto.Value} - ${cto.Description}`.toLowerCase().includes(mappedValue.toLowerCase()));
  }

  /**
   * This method is used to display the description of a UDF value in the autocomplete dropdown.
   * @param _udfValue 
   * @returns 
   */
  AutoCompleteDisplay(_udfValue: IUdfValue): string
  {
    return _udfValue ? _udfValue.Description : '';
  }

  /**
   * This method is called when the component receives an event to inflate UDFs with values.
   * @param _event 
   */
  OnInflateUDFs = (_event: ICLEvent) => {
    if(_event)
    {
      this.udfsInputValuesToSet = JSON.parse(_event.Data) as IUdfWithValue[];

      // Si los udfs ya estan cargados entonces procedo a setearles el valor indicado
      if(this.Udfs.length)
      {
        this.SetUdfsInputValues();
      }
    }
  }

  /**
   * Establece los valores provenientes del componente padre en su lugar de uso y los asigna a los User-Defined Fields (UDFs)
   * @constructor
   */
  SetUdfsInputValues(): void
  {
    if(!this.udfsInputValuesToSet || !this.udfsInputValuesToSet.length) return;

    // Le seteo el valor a todos los udfs
    this.udfsInputValuesToSet.forEach(u => {
      let udfIndex = this.Udfs.findIndex(d => d.Name === u.Name);
      if(udfIndex != -1)
      {

        this.Udfs[udfIndex].Value =  typeof(u.Value) === 'string' ? u.Value : u.Value;

        // Realizo el proceso de consulta de valores de un udf que depende de otro
        this.OnRenderedUdfChange(u.Value, this.Udfs[udfIndex]);
      }
    });
  }

  /**
   * Toma todos los udfs que se agregan al formulario que son de tipo typeahead para escuchar los cuando su valor cambia para filtrar las opciones
   * @constructor
   */
  ListenTypeaheadToFilterValues(): void
  {
    this.Udfs.filter(u => u.IsTypehead && u.IsRendered)
      .forEach(x => {
        this.renderedUdfsForm.get(x.Name)?.valueChanges
          .subscribe({
            next: (value: IUdfValue | string) => {
              if(value)
              {
                let mappedValue = typeof value === 'string' ? value : `${value.Value} - ${value.Description}`;
                this.filteredTypeheadOptions = this.currentTypeheadOptions.filter(cto => `${cto.Value} - ${cto.Description}`.toLowerCase().includes(mappedValue.toLowerCase()));
              }
            }
          });
      });
  }

}
